
import { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useDispatch } from 'react-redux';
import { loginSuccess } from '../redux/slices/authSlice';
import Cookies from 'js-cookie';
import { toast } from 'react-toastify';
import {
	Sofa,
	Smartphone,
	Shield,
	Clock,
	ArrowLeft,
	CheckCircle,
	User,
	Package,
	Sparkles,
	Crown,
	Truck,
	ShieldCheck,
	Phone,
	AlertCircle
} from 'lucide-react';

// Import Firebase
import { initializeApp } from 'firebase/app';
import {
	getAuth,
	RecaptchaVerifier,
	signInWithPhoneNumber,
	PhoneAuthProvider,
	signInWithCredential
} from 'firebase/auth';

// Firebase configuration
const firebaseConfig = {
	apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
	authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
	projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
	storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
	messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
	appId: process.env.REACT_APP_FIREBASE_APP_ID,
	measurementId: process.env.REACT_APP_FIREBASE_MEASUREMENT_ID
};

// Initialize Firebase
const app = initializeApp(firebaseConfig);
const auth = getAuth(app);

const Login = () => {
	const navigate = useNavigate();
	const dispatch = useDispatch();

	const [formData, setFormData] = useState({
		identifier: '',
		otp: ''
	});

	const [loading, setLoading] = useState(false);
	const [message, setMessage] = useState('');
	const [loginStep, setLoginStep] = useState(1);
	const [resendCooldown, setResendCooldown] = useState(0);
	const [isAccountSuspended, setIsAccountSuspended] = useState(false);
	const [animate, setAnimate] = useState(false);
	const [timer, setTimer] = useState(0);
	const [canResend, setCanResend] = useState(false);
	const [confirmationResult, setConfirmationResult] = useState(null);

	// Refs for cleanup
	const recaptchaVerifierRef = useRef(null);
	const recaptchaWidgetIdRef = useRef(null);

	// Initialize reCAPTCHA
	const initializeRecaptcha = () => {
		// Clear existing recaptcha if any
		if (recaptchaVerifierRef.current) {
			try {
				recaptchaVerifierRef.current.clear();
			} catch (error) {
				console.log('Error clearing old recaptcha:', error);
			}
			recaptchaVerifierRef.current = null;
		}

		// Create new reCAPTCHA verifier
		try {
			const verifier = new RecaptchaVerifier(auth, 'recaptcha-container', {
				size: 'invisible',
				callback: (response) => {
					console.log('reCAPTCHA solved');
				},
				'expired-callback': () => {
					console.log('reCAPTCHA expired');
					toast.info('reCAPTCHA expired. Please try again.');
				},
				'error-callback': (error) => {
					console.error('reCAPTCHA error:', error);
					toast.error('reCAPTCHA verification failed. Please reload the page.');
				}
			});

			recaptchaVerifierRef.current = verifier;

			// Render the reCAPTCHA
			verifier.render().then((widgetId) => {
				recaptchaWidgetIdRef.current = widgetId;
				console.log('reCAPTCHA widget rendered with ID:', widgetId);
			}).catch(error => {
				console.error('Error rendering reCAPTCHA:', error);
			});

			return verifier;
		} catch (error) {
			console.error('Error initializing reCAPTCHA:', error);
			toast.error('Failed to initialize security verification. Please reload the page.');
			return null;
		}
	};

	// Timer useEffect
	useEffect(() => {
		let interval;
		if (timer > 0) {
			interval = setInterval(() => {
				setTimer(prev => prev - 1);
			}, 1000);
		} else if (timer === 0 && loginStep === 2) {
			setCanResend(true);
		}
		return () => clearInterval(interval);
	}, [timer, loginStep]);

	// Resend OTP cooldown timer
	useEffect(() => {
		let cooldownTimer;
		if (resendCooldown > 0) {
			cooldownTimer = setInterval(() => {
				setResendCooldown(prev => prev - 1);
			}, 1000);
		}
		return () => clearInterval(cooldownTimer);
	}, [resendCooldown]);

	// Animation effect
	useEffect(() => {
		setAnimate(true);
		const animTimer = setTimeout(() => setAnimate(false), 1000);
		return () => clearTimeout(animTimer);
	}, [loginStep]);

	// Initialize reCAPTCHA on component mount
	useEffect(() => {
		// Small delay to ensure DOM is ready
		const timerId = setTimeout(() => {
			initializeRecaptcha();
		}, 100);

		// Cleanup on unmount
		return () => {
			clearTimeout(timerId);
			if (recaptchaVerifierRef.current) {
				try {
					recaptchaVerifierRef.current.clear();
				} catch (error) {
					console.log('Cleanup error:', error);
				}
			}
		};
	}, []);

	const validateForm = () => {
		const { identifier } = formData;

		if (loginStep === 1) {
			if (!identifier) {
				return 'Phone number is required.';
			}

			const phoneRegex = /^[0-9]{10}$/;
			if (!phoneRegex.test(identifier)) {
				return 'Enter a valid 10-digit phone number.';
			}
		}

		if (loginStep === 2 && !formData.otp) {
			return 'OTP is required.';
		}

		return '';
	};

	const handleChange = (e) => {
		setFormData(prev => ({
			...prev,
			[e.target.name]: e.target.value
		}));
	};

	const handleOtpChange = (e) => {
		const value = e.target.value.replace(/\D/g, '').slice(0, 6);
		setFormData(prev => ({
			...prev,
			otp: value
		}));
	};

	const sendLoginOtp = async (e) => {
		e.preventDefault();

		setMessage('');
		setIsAccountSuspended(false);

		const validationError = validateForm();
		if (validationError) {
			setMessage(validationError);
			return;
		}

		setLoading(true);
		try {
			const phoneNumber = `+91${formData.identifier}`;

			// Ensure reCAPTCHA is initialized
			if (!recaptchaVerifierRef.current) {
				initializeRecaptcha();
			}

			// Send OTP using Firebase
			const confirmation = await signInWithPhoneNumber(auth, phoneNumber, recaptchaVerifierRef.current);
			setConfirmationResult(confirmation);

			toast.success('OTP sent successfully to your phone!');

			setLoginStep(2);
			setTimer(60);
			setCanResend(false);
			setFormData(prev => ({ ...prev, otp: '' }));

		} catch (error) {
			console.error('Firebase OTP Error:', error);

			let errorMessage = 'Failed to send OTP. Please try again.';

			if (error.code === 'auth/invalid-phone-number') {
				errorMessage = 'Invalid phone number format.';
			} else if (error.code === 'auth/too-many-requests') {
				errorMessage = 'Too many attempts. Please try again later.';
			} else if (error.code === 'auth/quota-exceeded') {
				errorMessage = 'OTP quota exceeded. Please try again later.';
			} else if (error.code === 'auth/user-not-found') {
				errorMessage = 'User not registered. Please register first.';
			} else if (error.code === 'auth/captcha-check-failed' || error?.message?.includes('reCAPTCHA')) {
				errorMessage = 'Security verification failed. Please try again.';
			}

			setMessage(errorMessage);
			toast.error(errorMessage);

			// Reset reCAPTCHA on error
			if (recaptchaVerifierRef.current) {
				try {
					recaptchaVerifierRef.current.clear();
				} catch(e) {}
				initializeRecaptcha();
			}
		} finally {
			setLoading(false);
		}
	};

	const resendOtp = async () => {
		if (!canResend) return;

		setLoading(true);
		try {
			const phoneNumber = `+91${formData.identifier}`;

			// Ensure reCAPTCHA is initialized
			if (!recaptchaVerifierRef.current) {
				initializeRecaptcha();
			}

			// Send OTP using Firebase
			const confirmation = await signInWithPhoneNumber(auth, phoneNumber, recaptchaVerifierRef.current);
			setConfirmationResult(confirmation);

			toast.success('OTP resent successfully!');

			setTimer(60);
			setCanResend(false);
			setFormData(prev => ({ ...prev, otp: '' }));

		} catch (error) {
			console.error('Firebase Resend OTP Error:', error);

			let errorMessage = 'Failed to resend OTP. Please try again.';

			if (error.code === 'auth/too-many-requests') {
				errorMessage = 'Too many attempts. Please try again later.';
			} else if (error.code === 'auth/user-not-found') {
				errorMessage = 'User not registered. Please register first.';
			} else if (error.code === 'auth/captcha-check-failed' || error?.message?.includes('reCAPTCHA')) {
				errorMessage = 'Security check failed. Please try again.';
			}

			setMessage(errorMessage);
			toast.error(errorMessage);

			// Reset reCAPTCHA on error
			if (recaptchaVerifierRef.current) {
				try {
					recaptchaVerifierRef.current.clear();
				} catch(e) {}
				initializeRecaptcha();
			}
		} finally {
			setLoading(false);
		}
	};

	const verifyLoginOtp = async (e) => {
		e.preventDefault();

		if (formData.otp.length !== 6) {
			setMessage('OTP must be 6 digits');
			return;
		}

		setLoading(true);
		try {
			// Verify OTP with Firebase
			const credential = PhoneAuthProvider.credential(confirmationResult.verificationId, formData.otp);
			const userCredential = await signInWithCredential(auth, credential);

			// Get Firebase token
			const firebaseToken = await userCredential.user.getIdToken();

			// Call backend login endpoint with Firebase token
			const response = await axios.post(
				`${process.env.REACT_APP_BACKEND_URL}/api/buyer/login-with-phone`,
				{}, // Empty body since phone comes from Firebase token
				{
					headers: {
						'Authorization': `Bearer ${firebaseToken}`
					}
				}
			);

			if (response.data.token) {
				// Store JWT token
				localStorage.setItem('token', response.data.token);
				localStorage.setItem('user', JSON.stringify(response.data.user));

				// Update Redux store
				dispatch(
					loginSuccess({
						user: response.data.user,
						token: response.data.token
					})
				);

				// Set cookie
				Cookies.set('ecom_user_token', response.data.token, {
					expires: 60,
					secure: process.env.NODE_ENV === 'production',
					sameSite: 'Lax'
				});

				toast.success('Welcome to Zakkuu! Login successful.');

				// Clear reCAPTCHA on successful login
				if (recaptchaVerifierRef.current) {
					try {
						recaptchaVerifierRef.current.clear();
					} catch(e){}
				}

				navigate('/home');
			}
		} catch (error) {
			console.error('Login Error:', error);

			let errorMessage = 'Login failed. Please try again.';

			if (error.code === 'auth/invalid-verification-code') {
				errorMessage = 'Invalid OTP code. Please check and try again.';
			} else if (error.code === 'auth/code-expired') {
				errorMessage = 'OTP has expired. Please request a new one.';
			} else if (error.response?.data?.msg) {
				errorMessage = error.response.data.msg;

				// Check if user needs to register
				if (errorMessage === 'User not registered. Please register first.') {
					toast.info('User not registered. Redirecting to registration...');
					setTimeout(() => {
						navigate('/register', {
							state: {
								phone: formData.identifier,
								autoFill: true
							}
						});
					}, 2000);
					return;
				}
			}

			setMessage(errorMessage);
			toast.error(errorMessage);
		} finally {
			setLoading(false);
		}
	};

	const handleSubmit = async (e) => {
		e.preventDefault();

		const validationError = validateForm();
		if (validationError) {
			setMessage(validationError);
			return;
		}

		if (loginStep === 1) {
			await sendLoginOtp(e);
		} else {
			await verifyLoginOtp(e);
		}
	};

	const handleBack = () => {
		setLoginStep(1);
		setFormData(prev => ({ ...prev, otp: '' }));
		setMessage('');
		setIsAccountSuspended(false);
		setResendCooldown(0);
		setTimer(0);
		setCanResend(false);
		setConfirmationResult(null);

		// Reset reCAPTCHA
		if (recaptchaVerifierRef.current) {
			try {
				recaptchaVerifierRef.current.clear();
			} catch(e) {}
			initializeRecaptcha();
		}
	};

	const formatTime = (seconds) => {
		const mins = Math.floor(seconds / 60);
		const secs = seconds % 60;
		return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
	};

	return (
		<div className="min-h-screen flex flex-col lg:flex-row">
			{/* reCAPTCHA container */}
			<div id="recaptcha-container" style={{ display: 'none' }}></div>

			{/* Left side - Branding and Artisan Wood Theme - Hidden on mobile */}
			<div className="hidden lg:flex lg:w-1/2 bg-[#1A1614] p-8 lg:p-12 flex-col justify-between relative overflow-hidden border-r border-stone-800">

				{/* Artisan Textures - Subtle wood grain overlay */}
				<div className="absolute inset-0 opacity-[0.03] pointer-events-none bg-[url('https://www.transparenttextures.com/patterns/wood-pattern.png')]"></div>

				{/* Decorative Elements */}
				<div className="absolute inset-0 opacity-20">
					<div className="absolute top-10 left-10 w-64 h-64 border border-stone-800 rounded-full"></div>
					<div className="absolute -bottom-20 -right-20 w-96 h-96 border border-stone-800 rounded-full"></div>
					<div className="absolute top-1/2 left-1/3 w-32 h-32 border border-stone-800/50 rotate-45"></div>
				</div>

				{/* Logo and Brand */}
				<div className="relative z-10">
					<div className="flex items-center gap-4 mb-12">
						<div className="relative">
							<div className="w-14 h-14 bg-[#7A5C33] flex items-center justify-center shadow-2xl rounded-sm">
								<Sofa className="w-8 h-8 text-[#FDFBF7]" />
							</div>
							<div className="absolute -bottom-1 -right-1 w-6 h-6 bg-[#A38A64] rounded-full flex items-center justify-center border-2 border-[#1A1614]">
								<Sparkles className="w-3 h-3 text-white" />
							</div>
						</div>

						<div>
							<img src="/assets/zakkuu logo bg.png" alt="Logo" className="h-10 brightness-0 invert opacity-80" />
							<div className="flex items-center gap-2 mt-2">
								<div className="w-1.5 h-1.5 bg-[#A38A64] rounded-full animate-pulse"></div>
								<p className="text-stone-400 text-sm font-serif tracking-wide">Premium Furniture & Home Decor</p>
							</div>
						</div>
					</div>

					<div className="mb-8">
						<div className="inline-flex items-center gap-2 px-3 py-1 bg-stone-800/40 border border-stone-700 rounded-none mb-6">
							<Crown className="w-3.5 h-3.5 text-[#A38A64]" />
							<span className="text-[#A38A64] text-[10px] uppercase tracking-[0.2em] font-bold">Luxury Living</span>
						</div>
						<h2 className="text-4xl lg:text-5xl font-serif font-bold text-[#FDFBF7] mb-6 leading-tight">
							Welcome Back to
							<span className="block text-[#A38A64] italic">Your Dream Home</span>
						</h2>
						<p className="text-stone-300 text-lg font-serif">
							Continue your journey to a beautifully furnished home
						</p>
					</div>
				</div>

				{/* Features List */}
				<div className="relative z-10 grid grid-cols-1 md:grid-cols-2 gap-4 mt-8">
					{[
						{ icon: Package, title: "Secure Login", desc: "Firebase OTP protected" },
						{ icon: Truck, title: "Instant Access", desc: "One-tap verification" },
						{ icon: ShieldCheck, title: "Safe & Secure", desc: "256-bit encryption" },
						{ icon: CheckCircle, title: "Quick Login", desc: "No password needed" }
					].map((item, idx) => (
						<div key={idx} className="flex items-start gap-4 p-4 bg-stone-900/40 border border-stone-800/60 backdrop-blur-sm group hover:border-[#7A5C33] transition-all">
							<div className="p-2 bg-stone-800 group-hover:bg-[#7A5C33] transition-colors rounded-sm">
								<item.icon className="w-5 h-5 text-stone-300 group-hover:text-white" />
							</div>
							<div>
								<h4 className="font-serif font-bold text-[#FDFBF7]">{item.title}</h4>
								<p className="text-stone-500 text-xs mt-0.5">{item.desc}</p>
							</div>
						</div>
					))}
				</div>

				{/* Bottom Text */}
				<div className="relative z-10 mt-8">
					<div className="inline-block p-5 border-l-2 border-[#7A5C33] bg-stone-900/30 backdrop-blur-sm">
						<p className="text-stone-400 text-sm italic font-serif leading-relaxed">
							"Your style preferences and saved items are waiting for you"
						</p>
					</div>
				</div>
			</div>

			{/* Right side - Login Form - Full width on mobile */}
			<div className="w-full lg:w-1/2 p-4 sm:p-6 lg:p-12 flex items-center justify-center bg-[#FDFBF7] min-h-screen lg:min-h-0">
				{/* Background Texture */}
				<div className="absolute inset-0 opacity-[0.03] pointer-events-none bg-[url('https://www.transparenttextures.com/patterns/natural-paper.png')] lg:block"></div>

				<div className={`w-full max-w-md transition-all duration-500 z-10 ${animate ? 'scale-105' : 'scale-100'}`}>
					<div className="bg-white rounded-none shadow-[20px_20px_60px_#d9d7d3,-20px_-20px_60px_#ffffff] p-6 sm:p-8 border border-stone-200">

						{/* Mobile Header - Only visible on mobile */}
						<div className="lg:hidden flex items-center gap-3 mb-6 pb-6 border-b border-stone-100">
							<div className="w-10 h-10 bg-[#7A5C33] flex items-center justify-center rounded-sm">
								<Sofa className="w-5 h-5 text-[#FDFBF7]" />
							</div>
							<div>
								<img src="/assets/zakkuu logo bg.png" alt="Zakkuu" className="h-6" />
								<p className="text-stone-400 text-[10px] font-serif mt-0.5">Premium Furniture & Home Decor</p>
							</div>
						</div>

						{/* Form Header */}
						<div className="text-center mb-6 lg:mb-8">
							<div className="inline-flex items-center justify-center w-14 h-14 lg:w-16 lg:h-16 bg-stone-100 rounded-none mb-4 border border-stone-200">
								<div className="w-10 h-10 lg:w-12 lg:h-12 bg-[#7A5C33] rounded-none flex items-center justify-center shadow-inner">
									<User className="w-5 h-5 lg:w-6 lg:h-6 text-[#FDFBF7]" />
								</div>
							</div>
							<h2 className="text-xl sm:text-2xl lg:text-3xl font-serif font-bold text-stone-800 mb-2 uppercase tracking-tight">
								{loginStep === 1 ? 'Welcome Back' : 'Verify Identity'}
							</h2>
							<p className="text-stone-500 font-serif italic text-sm lg:text-base">
								{loginStep === 1
									? 'Sign in to your Zakkuu workshop'
									: 'Enter the secure code sent to your device'}
							</p>

							{/* Progress Indicator */}
							<div className="flex items-center justify-center gap-2 lg:gap-3 mt-4 lg:mt-6">
								<div className={`w-1.5 h-1.5 lg:w-2 lg:h-2 rotate-45 ${loginStep === 1 ? 'bg-[#7A5C33]' : 'bg-stone-300'}`}></div>
								<div className="w-16 lg:w-24 h-[1px] bg-stone-200 relative">
									<div className={`absolute top-0 left-0 h-full transition-all duration-500 ${loginStep === 1 ? 'w-1/2 bg-[#A38A64]' : 'w-full bg-[#7A5C33]'}`}></div>
								</div>
								<div className={`w-1.5 h-1.5 lg:w-2 lg:h-2 rotate-45 ${loginStep === 1 ? 'bg-stone-300' : 'bg-[#7A5C33]'}`}></div>
							</div>
						</div>

						{/* Account Suspended Alert */}
						{isAccountSuspended && (
							<div className="mb-6 p-4 bg-red-50 border-l-4 border-red-600 rounded-none">
								<div className="flex items-center gap-3">
									<AlertCircle className="w-5 h-5 text-red-600" />
									<div>
										<h4 className="font-bold text-red-800 text-xs uppercase tracking-wider">Access Restricted</h4>
										<p className="text-red-600 text-[10px] lg:text-xs">Please contact the master craftsman for support.</p>
									</div>
								</div>
							</div>
						)}

						{/* Security Header */}
						<div className="text-center mb-4 lg:mb-6">
							<div className="inline-flex items-center gap-2 px-2 lg:px-3 py-1 border border-stone-100 bg-stone-50 rounded-none">
								<Shield className="w-3 h-3 lg:w-3.5 lg:h-3.5 text-stone-400" />
								<span className="text-stone-500 text-[8px] lg:text-[10px] uppercase tracking-[0.15em] font-bold">
									Encrypted Connection
								</span>
							</div>
						</div>

						{/* Login Form */}
						<form onSubmit={handleSubmit} className="space-y-5 lg:space-y-6">
							{loginStep === 1 ? (
								<div className="space-y-4 lg:space-y-5">
									<div>
										<label className="flex items-center gap-2 text-[10px] lg:text-[11px] uppercase tracking-widest font-bold text-stone-600 mb-2">
											<Phone className="w-3 h-3 lg:w-3.5 lg:h-3.5 text-[#A38A64]" />
											Registered Phone
										</label>
										<div className="relative group">
											<div className="absolute inset-y-0 left-0 pl-3 lg:pl-4 flex items-center pointer-events-none border-r border-stone-200 my-2 pr-2 lg:pr-3">
												<span className="text-stone-400 font-serif text-sm lg:text-base">+91</span>
											</div>
											<input
												type="tel"
												name="identifier"
												value={formData.identifier}
												onChange={handleChange}
												className="w-full pl-14 lg:pl-16 pr-3 lg:pr-4 py-3 lg:py-3.5 bg-stone-50 border border-stone-200 rounded-none focus:outline-none focus:border-[#7A5C33] transition-all text-base lg:text-lg font-serif tracking-widest"
												placeholder="0000000000"
												maxLength={10}
												disabled={loading || isAccountSuspended}
											/>
										</div>
									</div>

									<button
										type="submit"
										disabled={loading || isAccountSuspended}
										className="w-full py-3 lg:py-4 bg-[#1A1614] text-[#FDFBF7] font-bold uppercase tracking-[0.2em] text-[10px] lg:text-xs rounded-none hover:bg-[#2A2624] transition-all flex items-center justify-center gap-3 shadow-xl disabled:opacity-50"
									>
										{loading ? <span className="animate-pulse">Requesting OTP...</span> : 'Continue'}
									</button>
								</div>
							) : (
								<div className="space-y-5 lg:space-y-6">
									<div className="text-center p-4 lg:p-5 bg-stone-50 border border-stone-100">
										<p className="text-stone-400 text-[10px] lg:text-xs uppercase tracking-widest mb-1">Code sent to</p>
										<p className="text-lg lg:text-xl font-serif text-stone-800 tracking-widest">
											+91 •••• {formData.identifier.slice(-4)}
										</p>
									</div>

									<div>
										<label className="block text-center text-[8px] lg:text-[10px] uppercase tracking-[0.2em] font-bold text-stone-500 mb-2 lg:mb-3">
											Enter 6-Digit Verification
										</label>
										<input
											type="text"
											name="otp"
											value={formData.otp}
											onChange={handleOtpChange}
											className="w-full py-3 lg:py-4 text-center text-2xl lg:text-3xl font-serif tracking-[0.5em] bg-stone-50 border border-stone-200 rounded-none focus:outline-none focus:border-[#7A5C33] transition-all"
											placeholder="000000"
											maxLength={6}
											inputMode="numeric"
											autoFocus
										/>
									</div>

									<div className="flex flex-col gap-3">
										<button
											type="submit"
											disabled={loading || isAccountSuspended || formData.otp.length !== 6}
											className="w-full py-3 lg:py-4 bg-[#7A5C33] text-white font-bold uppercase tracking-[0.2em] text-[10px] lg:text-xs rounded-none hover:bg-[#634a29] transition-all flex items-center justify-center gap-3 shadow-lg"
										>
											{loading ? 'Verifying...' : 'Verify & Enter'}
										</button>

										<div className="flex justify-between items-center mt-2">
											<button
												type="button"
												onClick={resendOtp}
												disabled={!canResend || loading}
												className="text-[8px] lg:text-[10px] uppercase tracking-widest font-bold text-stone-400 hover:text-[#A38A64] transition-colors disabled:opacity-30"
											>
												{canResend ? 'Resend Code' : `Wait ${formatTime(timer)}`}
											</button>
											<button
												type="button"
												onClick={handleBack}
												className="text-[8px] lg:text-[10px] uppercase tracking-widest font-bold text-stone-400 hover:text-stone-800 transition-colors"
											>
												Change Number
											</button>
										</div>
									</div>
								</div>
							)}
						</form>

						{/* Bottom Section */}
						{loginStep === 1 && (
							<div className="mt-6 lg:mt-8 pt-6 lg:pt-8 border-t border-stone-100 text-center">
								<p className="text-stone-400 text-[10px] lg:text-xs font-serif italic mb-3 lg:mb-4">New to the Zakkuu collections?</p>
								<a
									href="/register"
									className="inline-block w-full py-2.5 lg:py-3 border border-stone-800 text-stone-800 font-bold uppercase tracking-widest text-[8px] lg:text-[10px] hover:bg-stone-800 hover:text-white transition-all"
								>
									Create New Account
								</a>
							</div>
						)}

						{/* Security Footer */}
						<div className="mt-4 lg:mt-6 p-3 lg:p-3 bg-stone-50 border border-stone-100 flex items-start gap-2 lg:gap-3">
							<ShieldCheck className="w-4 h-4 lg:w-5 lg:h-5 text-[#A38A64] shrink-0" />
							<p className="text-[8px] lg:text-[10px] text-stone-500 leading-relaxed font-serif italic">
								Certified secure login. Your privacy is handcrafted into our system as carefully as our furniture.
							</p>
						</div>
					</div>
				</div>
			</div>
		</div>
	);
};

export default Login;

// import { useState, useEffect, useRef } from 'react';
// import { Link, useNavigate } from 'react-router-dom';
// import axios from 'axios';
// import { useDispatch } from 'react-redux';
// import { loginSuccess } from '../redux/slices/authSlice';
// import Cookies from 'js-cookie';
// import { toast } from 'react-toastify';
// import {
// 	Sofa,
// 	Smartphone,
// 	Shield,
// 	Clock,
// 	ArrowLeft,
// 	CheckCircle,
// 	User,
// 	Package,
// 	Sparkles,
// 	Crown,
// 	Truck,
// 	ShieldCheck,
// 	Phone,
// 	AlertCircle
// } from 'lucide-react';

// // Import Firebase
// import { initializeApp } from 'firebase/app';
// import {
// 	getAuth,
// 	RecaptchaVerifier,
// 	signInWithPhoneNumber,
// 	PhoneAuthProvider,
// 	signInWithCredential
// } from 'firebase/auth';

// // Firebase configuration
// const firebaseConfig = {
// 	apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
// 	authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
// 	projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
// 	storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
// 	messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
// 	appId: process.env.REACT_APP_FIREBASE_APP_ID,
// 	measurementId: process.env.REACT_APP_FIREBASE_MEASUREMENT_ID
// };

// // Initialize Firebase
// const app = initializeApp(firebaseConfig);
// const auth = getAuth(app);

// const Login = () => {
// 	const navigate = useNavigate();
// 	const dispatch = useDispatch();

// 	const [formData, setFormData] = useState({
// 		identifier: '',
// 		otp: ''
// 	});

// 	const [loading, setLoading] = useState(false);
// 	const [message, setMessage] = useState('');
// 	const [loginStep, setLoginStep] = useState(1);
// 	const [resendCooldown, setResendCooldown] = useState(0);
// 	const [isAccountSuspended, setIsAccountSuspended] = useState(false);
// 	const [animate, setAnimate] = useState(false);
// 	const [timer, setTimer] = useState(0);
// 	const [canResend, setCanResend] = useState(false);
// 	const [confirmationResult, setConfirmationResult] = useState(null);

// 	// Refs for cleanup
// 	const recaptchaVerifierRef = useRef(null);
// 	const recaptchaWidgetIdRef = useRef(null);

// 	// Initialize reCAPTCHA
// 	const initializeRecaptcha = () => {
// 		// Clear existing recaptcha if any
// 		if (recaptchaVerifierRef.current) {
// 			try {
// 				recaptchaVerifierRef.current.clear();
// 			} catch (error) {
// 				console.log('Error clearing old recaptcha:', error);
// 			}
// 			recaptchaVerifierRef.current = null;
// 		}

// 		// Create new reCAPTCHA verifier
// 		try {
// 			const verifier = new RecaptchaVerifier(auth, 'recaptcha-container', {
// 				size: 'invisible',
// 				callback: (response) => {
// 					console.log('reCAPTCHA solved:', response);
// 				},
// 				'expired-callback': () => {
// 					console.log('reCAPTCHA expired');
// 					toast.info('reCAPTCHA expired. Please try again.');
// 				},
// 				'error-callback': (error) => {
// 					console.error('reCAPTCHA error:', error);
// 					toast.error('reCAPTCHA verification failed. Please reload the page.');
// 				}
// 			});

// 			recaptchaVerifierRef.current = verifier;

// 			// Render the reCAPTCHA
// 			verifier.render().then((widgetId) => {
// 				recaptchaWidgetIdRef.current = widgetId;
// 				console.log('reCAPTCHA widget rendered with ID:', widgetId);
// 			}).catch(error => {
// 				console.error('Error rendering reCAPTCHA:', error);
// 			});

// 			return verifier;
// 		} catch (error) {
// 			console.error('Error initializing reCAPTCHA:', error);
// 			toast.error('Failed to initialize security verification. Please reload the page.');
// 			return null;
// 		}
// 	};

// 	// Timer useEffect
// 	useEffect(() => {
// 		let interval;
// 		if (timer > 0) {
// 			interval = setInterval(() => {
// 				setTimer(prev => prev - 1);
// 			}, 1000);
// 		} else if (timer === 0 && loginStep === 2) {
// 			setCanResend(true);
// 		}
// 		return () => clearInterval(interval);
// 	}, [timer, loginStep]);

// 	// Resend OTP cooldown timer
// 	useEffect(() => {
// 		let timer;
// 		if (resendCooldown > 0) {
// 			timer = setInterval(() => {
// 				setResendCooldown(prev => prev - 1);
// 			}, 1000);
// 		}
// 		return () => clearInterval(timer);
// 	}, [resendCooldown]);

// 	// Animation effect
// 	useEffect(() => {
// 		setAnimate(true);
// 		const timer = setTimeout(() => setAnimate(false), 1000);
// 		return () => clearTimeout(timer);
// 	}, [loginStep]);

// 	// Initialize reCAPTCHA on component mount
// 	useEffect(() => {
// 		const initializeOnMount = async () => {
// 			// Small delay to ensure DOM is ready
// 			await new Promise(resolve => setTimeout(resolve, 100));

// 			if (!document.getElementById('recaptcha-container')) {
// 				// Create container if it doesn't exist
// 				const container = document.createElement('div');
// 				container.id = 'recaptcha-container';
// 				container.style.display = 'none';
// 				document.body.appendChild(container);
// 			}

// 			// Initialize reCAPTCHA
// 			initializeRecaptcha();
// 		};

// 		initializeOnMount();

// 		// Cleanup on unmount
// 		return () => {
// 			if (recaptchaVerifierRef.current) {
// 				try {
// 					recaptchaVerifierRef.current.clear();
// 				} catch (error) {
// 					console.log('Cleanup error:', error);
// 				}
// 			}
// 			const container = document.getElementById('recaptcha-container');
// 			if (container && container.parentNode) {
// 				container.parentNode.removeChild(container);
// 			}
// 		};
// 	}, []);

// 	const validateForm = () => {
// 		const { identifier } = formData;

// 		if (loginStep === 1) {
// 			if (!identifier) {
// 				return 'Phone number is required.';
// 			}

// 			const phoneRegex = /^[0-9]{10}$/;
// 			if (!phoneRegex.test(identifier)) {
// 				return 'Enter a valid 10-digit phone number.';
// 			}
// 		}

// 		if (loginStep === 2 && !formData.otp) {
// 			return 'OTP is required.';
// 		}

// 		return '';
// 	};

// 	const handleChange = (e) => {
// 		setFormData(prev => ({
// 			...prev,
// 			[e.target.name]: e.target.value
// 		}));
// 	};

// 	const handleOtpChange = (e) => {
// 		const value = e.target.value.replace(/\D/g, '').slice(0, 6);
// 		setFormData(prev => ({
// 			...prev,
// 			otp: value
// 		}));
// 	};

// 	const sendLoginOtp = async (e) => {
// 		e.preventDefault();

// 		setMessage('');
// 		setIsAccountSuspended(false);

// 		const validationError = validateForm();
// 		if (validationError) {
// 			setMessage(validationError);
// 			return;
// 		}

// 		setLoading(true);
// 		try {
// 			// Format phone number for Firebase
// 			const phoneNumber = `+91${formData.identifier}`;

// 			// Ensure reCAPTCHA is initialized
// 			if (!recaptchaVerifierRef.current) {
// 				initializeRecaptcha();
// 			}

// 			// Get reCAPTCHA token
// 			const recaptchaToken = await recaptchaVerifierRef.current.verify();
// 			console.log('reCAPTCHA token obtained:', recaptchaToken ? 'Yes' : 'No');

// 			// Send OTP using Firebase
// 			const confirmation = await signInWithPhoneNumber(auth, phoneNumber, recaptchaVerifierRef.current);
// 			setConfirmationResult(confirmation);

// 			toast.success('OTP sent successfully to your phone!');

// 			setLoginStep(2);
// 			setTimer(60);
// 			setCanResend(false);
// 			setFormData(prev => ({ ...prev, otp: '' }));

// 			// Reset reCAPTCHA for next use
// 			if (recaptchaVerifierRef.current) {
// 				recaptchaVerifierRef.current.clear();
// 				initializeRecaptcha();
// 			}
// 		} catch (error) {
// 			console.error('Firebase OTP Error:', error);

// 			let errorMessage = 'Failed to send OTP. Please try again.';

// 			if (error.code === 'auth/invalid-phone-number') {
// 				errorMessage = 'Invalid phone number format.';
// 			} else if (error.code === 'auth/too-many-requests') {
// 				errorMessage = 'Too many attempts. Please try again later.';
// 			} else if (error.code === 'auth/quota-exceeded') {
// 				errorMessage = 'OTP quota exceeded. Please try again later.';
// 			} else if (error.code === 'auth/user-not-found') {
// 				errorMessage = 'User not registered. Please register first.';
// 			} else if (error.code === 'auth/captcha-check-failed') {
// 				errorMessage = 'Security check failed. Please reload the page.';
// 				// Reinitialize reCAPTCHA on captcha failure
// 				initializeRecaptcha();
// 			} else if (error.message.includes('reCAPTCHA')) {
// 				errorMessage = 'Security verification failed. Please reload the page.';
// 				initializeRecaptcha();
// 			}

// 			setMessage(errorMessage);
// 			toast.error(errorMessage);

// 			// Reset reCAPTCHA on error
// 			if (recaptchaVerifierRef.current) {
// 				recaptchaVerifierRef.current.clear();
// 				initializeRecaptcha();
// 			}
// 		} finally {
// 			setLoading(false);
// 		}
// 	};

// 	const resendOtp = async () => {
// 		if (!canResend) return;

// 		setLoading(true);
// 		try {
// 			const phoneNumber = `+91${formData.identifier}`;

// 			// Ensure reCAPTCHA is initialized
// 			if (!recaptchaVerifierRef.current) {
// 				initializeRecaptcha();
// 			}

// 			// Get reCAPTCHA token
// 			await recaptchaVerifierRef.current.verify();

// 			const confirmation = await signInWithPhoneNumber(auth, phoneNumber, recaptchaVerifierRef.current);
// 			setConfirmationResult(confirmation);

// 			toast.success('OTP resent successfully!');

// 			setTimer(60);
// 			setCanResend(false);
// 			setFormData(prev => ({ ...prev, otp: '' }));

// 			// Reset reCAPTCHA
// 			if (recaptchaVerifierRef.current) {
// 				recaptchaVerifierRef.current.clear();
// 				initializeRecaptcha();
// 			}
// 		} catch (error) {
// 			console.error('Firebase Resend OTP Error:', error);

// 			let errorMessage = 'Failed to resend OTP. Please try again.';

// 			if (error.code === 'auth/too-many-requests') {
// 				errorMessage = 'Too many attempts. Please try again later.';
// 			} else if (error.code === 'auth/user-not-found') {
// 				errorMessage = 'User not registered. Please register first.';
// 			} else if (error.code === 'auth/captcha-check-failed') {
// 				errorMessage = 'Security check failed. Please reload the page.';
// 				initializeRecaptcha();
// 			}

// 			setMessage(errorMessage);
// 			toast.error(errorMessage);

// 			// Reset reCAPTCHA on error
// 			if (recaptchaVerifierRef.current) {
// 				recaptchaVerifierRef.current.clear();
// 				initializeRecaptcha();
// 			}
// 		} finally {
// 			setLoading(false);
// 		}
// 	};

// 	const verifyLoginOtp = async (e) => {
// 		e.preventDefault();

// 		if (formData.otp.length !== 6) {
// 			setMessage('OTP must be 6 digits');
// 			return;
// 		}

// 		setLoading(true);
// 		try {
// 			// Verify OTP with Firebase
// 			const credential = PhoneAuthProvider.credential(confirmationResult.verificationId, formData.otp);
// 			const userCredential = await signInWithCredential(auth, credential);

// 			// Get Firebase token
// 			const firebaseToken = await userCredential.user.getIdToken();
// 			const phoneNumber = userCredential.user.phoneNumber.replace('+91', '');

// 			// Call backend login endpoint with Firebase token
// 			const response = await axios.post(
// 				`${process.env.REACT_APP_BACKEND_URL}/api/buyer/login-with-phone`,
// 				{}, // Empty body since phone comes from Firebase token
// 				{
// 					headers: {
// 						'Authorization': `Bearer ${firebaseToken}`
// 					}
// 				}
// 			);

// 			if (response.data.token) {
// 				// Store JWT token
// 				localStorage.setItem('token', response.data.token);
// 				localStorage.setItem('user', JSON.stringify(response.data.user));

// 				// Update Redux store
// 				dispatch(
// 					loginSuccess({
// 						user: response.data.user,
// 						token: response.data.token
// 					})
// 				);

// 				// Set cookie
// 				Cookies.set('ecom_user_token', response.data.token, {
// 					expires: 60,
// 					secure: process.env.NODE_ENV === 'production',
// 					sameSite: 'Lax'
// 				});

// 				toast.success('Welcome to Zakkuu! Login successful.');

// 				// Clear reCAPTCHA on successful login
// 				if (recaptchaVerifierRef.current) {
// 					recaptchaVerifierRef.current.clear();
// 				}

// 				navigate('/home');
// 			}
// 		} catch (error) {
// 			console.error('Login Error:', error);

// 			let errorMessage = 'Login failed. Please try again.';

// 			if (error.code === 'auth/invalid-verification-code') {
// 				errorMessage = 'Invalid OTP code. Please check and try again.';
// 			} else if (error.code === 'auth/code-expired') {
// 				errorMessage = 'OTP has expired. Please request a new one.';
// 			} else if (error.response?.data?.msg) {
// 				errorMessage = error.response.data.msg;

// 				// Check if user needs to register
// 				if (errorMessage === 'User not registered. Please register first.') {
// 					toast.info('User not registered. Redirecting to registration...');
// 					setTimeout(() => {
// 						navigate('/register', {
// 							state: {
// 								phone: formData.identifier,
// 								autoFill: true
// 							}
// 						});
// 					}, 2000);
// 					return;
// 				}
// 			}

// 			setMessage(errorMessage);
// 			toast.error(errorMessage);
// 		} finally {
// 			setLoading(false);
// 		}
// 	};

// 	const handleSubmit = async (e) => {
// 		e.preventDefault();

// 		const validationError = validateForm();
// 		if (validationError) {
// 			setMessage(validationError);
// 			return;
// 		}

// 		if (loginStep === 1) {
// 			await sendLoginOtp(e);
// 		} else {
// 			await verifyLoginOtp(e);
// 		}
// 	};

// 	const handleBack = () => {
// 		setLoginStep(1);
// 		setFormData(prev => ({ ...prev, otp: '' }));
// 		setMessage('');
// 		setIsAccountSuspended(false);
// 		setResendCooldown(0);
// 		setTimer(0);
// 		setCanResend(false);
// 		setConfirmationResult(null);

// 		// Reset reCAPTCHA
// 		if (recaptchaVerifierRef.current) {
// 			recaptchaVerifierRef.current.clear();
// 			initializeRecaptcha();
// 		}
// 	};

// 	const formatTime = (seconds) => {
// 		const mins = Math.floor(seconds / 60);
// 		const secs = seconds % 60;
// 		return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
// 	};

// 	return (
// 		<div className="min-h-screen flex flex-col lg:flex-row">
// 			{/* Hidden reCAPTCHA container - This will be created dynamically */}
// 			<div id="recaptcha-container" style={{ display: 'none' }}></div>

// 			{/* Left side - Branding and Artisan Wood Theme */}
// 			<div className="lg:w-1/2 bg-[#1A1614] p-8 lg:p-12 flex flex-col justify-between relative overflow-hidden border-r border-stone-800">

// 				{/* Artisan Textures - Subtle wood grain overlay */}
// 				<div className="absolute inset-0 opacity-[0.03] pointer-events-none bg-[url('https://www.transparenttextures.com/patterns/wood-pattern.png')]"></div>

// 				{/* Decorative Elements - Swapped digital glows for "Etched" linework */}
// 				<div className="absolute inset-0 opacity-20">
// 					<div className="absolute top-10 left-10 w-64 h-64 border border-stone-800 rounded-full"></div>
// 					<div className="absolute -bottom-20 -right-20 w-96 h-96 border border-stone-800 rounded-full"></div>
// 					<div className="absolute top-1/2 left-1/3 w-32 h-32 border border-stone-800/50 rotate-45"></div>
// 				</div>

// 				{/* Logo and Brand */}
// 				<div className="relative z-10">
// 					<div className="flex items-center gap-4 mb-12">
// 						<div className="relative">
// 							{/* Icon Box - Styled like a branded wood block */}
// 							<div className="w-14 h-14 bg-[#7A5C33] flex items-center justify-center shadow-2xl rounded-sm">
// 								<Sofa className="w-8 h-8 text-[#FDFBF7]" />
// 							</div>
// 							{/* Sparkle - Antiqued Gold accent */}
// 							<div className="absolute -bottom-1 -right-1 w-6 h-6 bg-[#A38A64] rounded-full flex items-center justify-center border-2 border-[#1A1614]">
// 								<Sparkles className="w-3 h-3 text-white" />
// 							</div>
// 						</div>

// 						<div>
// 							{/* Logo Image - Brightened for the dark wood background */}
// 							<img src="/assets/zakkuu logo bg.png" alt="Logo" className="h-10 brightness-0 invert opacity-80" />
// 							<div className="flex items-center gap-2 mt-2">
// 								<div className="w-1.5 h-1.5 bg-[#A38A64] rounded-full animate-pulse"></div>
// 								<p className="text-stone-400 text-sm font-serif tracking-wide">Premium Furniture & Home Decor</p>
// 							</div>
// 						</div>
// 					</div>

// 					<div className="mb-8">
// 						<div className="inline-flex items-center gap-2 px-3 py-1 bg-stone-800/40 border border-stone-700 rounded-none mb-6">
// 							<Crown className="w-3.5 h-3.5 text-[#A38A64]" />
// 							<span className="text-[#A38A64] text-[10px] uppercase tracking-[0.2em] font-bold">Luxury Living</span>
// 						</div>
// 						<h2 className="text-4xl lg:text-5xl font-serif font-bold text-[#FDFBF7] mb-6 leading-tight">
// 							Welcome Back to
// 							<span className="block text-[#A38A64] italic">Your Dream Home</span>
// 						</h2>
// 						<p className="text-stone-300 text-lg font-serif">
// 							Continue your journey to a beautifully furnished home
// 						</p>
// 					</div>
// 				</div>

// 				{/* Features List - "Workshop Drawer" Style */}
// 				<div className="relative z-10 grid grid-cols-1 md:grid-cols-2 gap-4 mt-8">
// 					{[
// 						{ icon: Package, title: "Secure Login", desc: "Firebase OTP protected" },
// 						{ icon: Truck, title: "Instant Access", desc: "One-tap verification" },
// 						{ icon: ShieldCheck, title: "Safe & Secure", desc: "256-bit encryption" },
// 						{ icon: CheckCircle, title: "Quick Login", desc: "No password needed" }
// 					].map((item, idx) => (
// 						<div key={idx} className="flex items-start gap-4 p-4 bg-stone-900/40 border border-stone-800/60 backdrop-blur-sm group hover:border-[#7A5C33] transition-all">
// 							<div className="p-2 bg-stone-800 group-hover:bg-[#7A5C33] transition-colors rounded-sm">
// 								<item.icon className="w-5 h-5 text-stone-300 group-hover:text-white" />
// 							</div>
// 							<div>
// 								<h4 className="font-serif font-bold text-[#FDFBF7]">{item.title}</h4>
// 								<p className="text-stone-500 text-xs mt-0.5">{item.desc}</p>
// 							</div>
// 						</div>
// 					))}
// 				</div>

// 				{/* Bottom Text - Framed Signature look */}
// 				<div className="relative z-10 mt-8">
// 					<div className="inline-block p-5 border-l-2 border-[#7A5C33] bg-stone-900/30 backdrop-blur-sm">
// 						<p className="text-stone-400 text-sm italic font-serif leading-relaxed">
// 							"Your style preferences and saved items are waiting for you"
// 						</p>
// 					</div>
// 				</div>
// 			</div>

// 			{/* Right side - Login Form */}
// 			<div className="lg:w-1/2 p-6 sm:p-8 lg:p-12 flex items-center justify-center bg-[#FDFBF7]">
// 				{/* Background Texture for the form side */}
// 				<div className="absolute inset-0 opacity-[0.03] pointer-events-none bg-[url('https://www.transparenttextures.com/patterns/natural-paper.png')]"></div>

// 				<div className={`w-full max-w-md transition-all duration-500 z-10 ${animate ? 'scale-105' : 'scale-100'}`}>
// 					<div className="bg-white rounded-none shadow-[20px_20px_60px_#d9d7d3,-20px_-20px_60px_#ffffff] p-6 sm:p-8 border border-stone-200">

// 						{/* Form Header */}
// 						<div className="text-center mb-8">
// 							<div className="inline-flex items-center justify-center w-16 h-16 bg-stone-100 rounded-none mb-4 border border-stone-200">
// 								<div className="w-12 h-12 bg-[#7A5C33] rounded-none flex items-center justify-center shadow-inner">
// 									<User className="w-6 h-6 text-[#FDFBF7]" />
// 								</div>
// 							</div>
// 							<h2 className="text-2xl sm:text-3xl font-serif font-bold text-stone-800 mb-2 uppercase tracking-tight">
// 								{loginStep === 1 ? 'Welcome Back' : 'Verify Identity'}
// 							</h2>
// 							<p className="text-stone-500 font-serif italic">
// 								{loginStep === 1
// 									? 'Sign in to your Zakkuu workshop'
// 									: 'Enter the secure code sent to your device'}
// 							</p>

// 							{/* Progress Indicator - Styled like a measuring ruler */}
// 							<div className="flex items-center justify-center gap-3 mt-6">
// 								<div className={`w-2 h-2 rotate-45 ${loginStep === 1 ? 'bg-[#7A5C33]' : 'bg-stone-300'}`}></div>
// 								<div className="w-24 h-[1px] bg-stone-200 relative">
// 									<div className={`absolute top-0 left-0 h-full transition-all duration-500 ${loginStep === 1 ? 'w-1/2 bg-[#A38A64]' : 'w-full bg-[#7A5C33]'}`}></div>
// 								</div>
// 								<div className={`w-2 h-2 rotate-45 ${loginStep === 1 ? 'bg-stone-300' : 'bg-[#7A5C33]'}`}></div>
// 							</div>
// 						</div>

// 						{/* Account Suspended Alert */}
// 						{isAccountSuspended && (
// 							<div className="mb-6 p-4 bg-red-50 border-l-4 border-red-600 rounded-none">
// 								<div className="flex items-center gap-3">
// 									<AlertCircle className="w-5 h-5 text-red-600" />
// 									<div>
// 										<h4 className="font-bold text-red-800 text-sm uppercase tracking-wider">Access Restricted</h4>
// 										<p className="text-red-600 text-xs">Please contact the master craftsman for support.</p>
// 									</div>
// 								</div>
// 							</div>
// 						)}

// 						{/* Security Header */}
// 						<div className="text-center mb-6">
// 							<div className="inline-flex items-center gap-2 px-3 py-1 border border-stone-100 bg-stone-50 rounded-none">
// 								<Shield className="w-3.5 h-3.5 text-stone-400" />
// 								<span className="text-stone-500 text-[10px] uppercase tracking-[0.15em] font-bold">
// 									Encrypted Connection
// 								</span>
// 							</div>
// 						</div>

// 						{/* Login Form */}
// 						<form onSubmit={handleSubmit} className="space-y-6">
// 							{loginStep === 1 ? (
// 								<div className="space-y-5">
// 									<div>
// 										<label className="flex items-center gap-2 text-[11px] uppercase tracking-widest font-bold text-stone-600 mb-2">
// 											<Phone className="w-3.5 h-3.5 text-[#A38A64]" />
// 											Registered Phone
// 										</label>
// 										<div className="relative group">
// 											<div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none border-r border-stone-200 my-2 pr-3">
// 												<span className="text-stone-400 font-serif">+91</span>
// 											</div>
// 											<input
// 												type="tel"
// 												name="identifier"
// 												value={formData.identifier}
// 												onChange={handleChange}
// 												className="w-full pl-16 pr-4 py-3.5 bg-stone-50 border border-stone-200 rounded-none focus:outline-none focus:border-[#7A5C33] transition-all text-lg font-serif tracking-widest"
// 												placeholder="0000000000"
// 												maxLength={10}
// 												disabled={loading || isAccountSuspended}
// 											/>
// 										</div>
// 									</div>

// 									<button
// 										type="submit"
// 										disabled={loading || isAccountSuspended}
// 										className="w-full py-4 bg-[#1A1614] text-[#FDFBF7] font-bold uppercase tracking-[0.2em] text-xs rounded-none hover:bg-[#2A2624] transition-all flex items-center justify-center gap-3 shadow-xl disabled:opacity-50"
// 									>
// 										{loading ? <span className="animate-pulse">Requesting OTP...</span> : 'Continue'}
// 									</button>
// 								</div>
// 							) : (
// 								<div className="space-y-6">
// 									<div className="text-center p-5 bg-stone-50 border border-stone-100">
// 										<p className="text-stone-400 text-xs uppercase tracking-widest mb-1">Code sent to</p>
// 										<p className="text-xl font-serif text-stone-800 tracking-widest">
// 											+91 •••• {formData.identifier.slice(-4)}
// 										</p>
// 									</div>

// 									<div>
// 										<label className="block text-center text-[10px] uppercase tracking-[0.2em] font-bold text-stone-500 mb-3">
// 											Enter 6-Digit Verification
// 										</label>
// 										<input
// 											type="text"
// 											name="otp"
// 											value={formData.otp}
// 											onChange={handleOtpChange}
// 											className="w-full py-4 text-center text-3xl font-serif tracking-[0.5em] bg-stone-50 border border-stone-200 rounded-none focus:outline-none focus:border-[#7A5C33] transition-all"
// 											placeholder="000000"
// 											maxLength={6}
// 											inputMode="numeric"
// 											autoFocus
// 										/>
// 									</div>

// 									<div className="flex flex-col gap-3">
// 										<button
// 											type="submit"
// 											disabled={loading || isAccountSuspended || formData.otp.length !== 6}
// 											className="w-full py-4 bg-[#7A5C33] text-white font-bold uppercase tracking-[0.2em] text-xs rounded-none hover:bg-[#634a29] transition-all flex items-center justify-center gap-3 shadow-lg"
// 										>
// 											{loading ? 'Verifying...' : 'Verify & Enter'}
// 										</button>

// 										<div className="flex justify-between items-center mt-2">
// 											<button
// 												type="button"
// 												onClick={resendOtp}
// 												disabled={!canResend || loading}
// 												className="text-[10px] uppercase tracking-widest font-bold text-stone-400 hover:text-[#A38A64] transition-colors disabled:opacity-30"
// 											>
// 												{canResend ? 'Resend Code' : `Wait ${formatTime(timer)}`}
// 											</button>
// 											<button
// 												type="button"
// 												onClick={handleBack}
// 												className="text-[10px] uppercase tracking-widest font-bold text-stone-400 hover:text-stone-800 transition-colors"
// 											>
// 												Change Number
// 											</button>
// 										</div>
// 									</div>
// 								</div>
// 							)}
// 						</form>

// 						{/* Bottom Section */}
// 						{loginStep === 1 && (
// 							<div className="mt-8 pt-8 border-t border-stone-100 text-center">
// 								<p className="text-stone-400 text-xs font-serif italic mb-4">New to the Zakkuu collections?</p>
// 								<a
// 									href="/register"
// 									className="inline-block w-full py-3 border border-stone-800 text-stone-800 font-bold uppercase tracking-widest text-[10px] hover:bg-stone-800 hover:text-white transition-all"
// 								>
// 									Create New Account
// 								</a>
// 							</div>
// 						)}

// 						{/* Security Footer */}
// 						<div className="mt-6 p-3 bg-stone-50 border border-stone-100 flex items-start gap-3">
// 							<ShieldCheck className="w-5 h-5 text-[#A38A64] shrink-0" />
// 							<p className="text-[10px] text-stone-500 leading-relaxed font-serif italic">
// 								Certified secure login. Your privacy is handcrafted into our system as carefully as our furniture.
// 							</p>
// 						</div>
// 					</div>
// 				</div>
// 			</div>
// 		</div>
// 	);
// };

// export default Login;